/**
 * Slug del tenant por Host — cookie (client) + cache sync (SSR).
 * Sin `node:async_hooks`: este módulo entra al bundle del browser (rewrite / storefrontPath).
 * El ALS vive en `tenant-host-slug.server.ts` (solo middleware).
 *
 * @see tenant-host-rewrite.ts
 */
import { normalizeHost, parseTenantHost } from "@/lib/tenant-host";

export const TENANT_HOST_SLUG_COOKIE = "sm_host_slug";

export type HostSlugStore = {
  host: string;
  slug: string;
};

/** Cache sync host → slug (TTL corto). Compartido middleware ↔ rewrite. */
const SLUG_CACHE_TTL_MS = 60_000;
const slugByHostCache = new Map<string, { slug: string; exp: number }>();

/** Inyectado solo en server por `tenant-host-slug.server.ts`. */
let alsStoreGetter: (() => HostSlugStore | null | undefined) | null = null;

/** Solo server: registra el getter de ALS (evita importar async_hooks en client). */
export function registerHostSlugAlsGetter(
  getter: () => HostSlugStore | null | undefined,
): void {
  alsStoreGetter = getter;
}

export function rememberHostSlug(host: string, slug: string): void {
  const h = normalizeHost(host);
  if (!h || !slug) return;
  slugByHostCache.set(h, { slug, exp: Date.now() + SLUG_CACHE_TTL_MS });
}

export function cachedHostSlug(hostname: string): string | null {
  const h = normalizeHost(hostname);
  if (!h) return null;
  const hit = slugByHostCache.get(h);
  if (!hit) return null;
  if (hit.exp <= Date.now()) {
    slugByHostCache.delete(h);
    return null;
  }
  return hit.slug;
}

export function readHostSlugCookie(cookieHeader: string | null | undefined): string | null {
  if (!cookieHeader) return null;
  const parts = cookieHeader.split(";");
  for (const part of parts) {
    const [rawKey, ...rest] = part.trim().split("=");
    if (rawKey !== TENANT_HOST_SLUG_COOKIE) continue;
    const raw = rest.join("=").trim();
    if (!raw) return null;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
}

export function hostSlugCookieHeader(slug: string): string {
  return `${TENANT_HOST_SLUG_COOKIE}=${encodeURIComponent(slug)}; Path=/; Max-Age=86400; SameSite=Lax`;
}

/** Slug sync para rewrites: ALS (server) → cookie (client) → cache host. */
export function peekTenantHostSlug(hostname: string): string | null {
  const parsed = parseTenantHost(hostname);
  if (parsed.kind === "platform") return null;

  if (typeof document !== "undefined") {
    return readHostSlugCookie(document.cookie) ?? cachedHostSlug(hostname);
  }

  const store = alsStoreGetter?.() ?? null;
  if (store?.slug) {
    if (store.host && normalizeHost(hostname) && store.host !== normalizeHost(hostname)) {
      return null;
    }
    return store.slug;
  }

  return cachedHostSlug(hostname);
}

/** Slug del host tenant actual (ALS / cookie / cache). Null en host plataforma. */
export function currentTenantHostSlug(): string | null {
  if (typeof document !== "undefined") {
    return peekTenantHostSlug(window.location.hostname);
  }
  return alsStoreGetter?.()?.slug ?? null;
}
